import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { faqAnswers } from "../../data/faq-answers"
import faqData from "../../data/faq-data.json"
import { FAQAccordion } from "./FAQAccordion"
import { LandingChipSectionHeader } from "./LandingChipSectionHeader"

// Export constants for external reference
export const HOW_BRACKETS_WORK_FAQ_ID = faqData.constants.howBracketsWorkFaqId
export const WHATS_BRACKY_FAQ_ID = faqData.constants.whatsBrackyFaqId

const FAQSection = memo(function FAQSection({
  section,
  sectionIndex,
  openId,
  onToggleFAQ,
}: {
  section: (typeof faqData.sections)[0]
  sectionIndex: number
  openId: string | null
  onToggleFAQ: (id: string) => void
}) {
  const brackyWink = faqData.constants.brackyWink

  return (
    <div>
      {/* Section heading */}
      <h2 className="text-h3 text-white font-medium mb-4 flex items-center gap-2 ml-3">
        {section.title.includes(";)") ? (
          <>
            {section.title.split(";)")[0]?.trim()} <span className="text-[#30DFFF]">;)</span>
          </>
        ) : (
          <>
            {section.title}
            {section.title === "How It Works" && (
              <img
                src={brackyWink}
                alt="Bracky wink"
                className="w-[2.4rem] h-[2.4rem] ml-[.1rem] translate-y-0 inline-block"
              />
            )}
          </>
        )}
      </h2>

      {/* FAQs within the section */}
      <div className="space-y-4">
        {section.items.map((faq, itemIndex) => {
          const id = faq.id || `${sectionIndex}-${itemIndex}`
          const isOpen = openId === id
          const answer = faq.type === "rich" ? (faqAnswers[id] ?? "") : (faq.answer ?? "")

          return (
            <FAQAccordion
              key={id}
              id={id}
              question={faq.question}
              answer={answer || "Coming soon..."}
              isOpen={isOpen}
              onToggle={onToggleFAQ}
              updatedAt={faq.updatedAt}
            />
          )
        })}
      </div>
    </div>
  )
})

export const LandingSectionFAQ = memo(function LandingSectionFAQ() {
  // Track the currently open question by a unique id
  // Default open first FAQ (How It Works → "What's Bracket?")
  const [openId, setOpenId] = useState<string | null>("0-0")

  // Toggle an FAQ card
  const toggleFAQ = useCallback((id: string) => {
    setOpenId((prevId) => (prevId === id ? null : id))
  }, [])

  // Open a specific FAQ via external trigger
  const scrollToAndOpenFAQ = useCallback((targetId: string) => {
    // Open the requested FAQ
    setOpenId(targetId)
    // After opening, ensure we center it after the DOM updates/render
    setTimeout(() => {
      const el = document.getElementById(`faq-${targetId}`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 50)
  }, [])

  // Listen for custom events to open specific FAQs
  useEffect(() => {
    const handleOpenFAQ = (event: CustomEvent) => {
      scrollToAndOpenFAQ(event.detail.faqId)
    }

    window.addEventListener("openFAQ", handleOpenFAQ as EventListener)
    return () => window.removeEventListener("openFAQ", handleOpenFAQ as EventListener)
  }, [scrollToAndOpenFAQ])

  // Filter sections based on showAllSections flag
  const visibleSections = useMemo(() => {
    return faqData.showAllSections
      ? faqData.sections
      : faqData.sections.filter((section) => section.title === "How It Works")
  }, [])

  return (
    <section
      id="faqs"
      className="relative isolate z-30 pt-0 sm:pt-10 px-0 sm:px-4 lg:px-6 scroll-mt-[18vh] sm:scroll-mt-[12vh] lg:scroll-mt-[13vh] tall:scroll-mt-[10vh]"
    >
      <div className="w-full max-w-[90%] sm:max-w-md lg:max-w-124 xl:max-w-124 2xl:max-w-124 mx-auto">
        <LandingChipSectionHeader label="THE PLAYBOOK" wrapperClassName="" />

        {/* Section header */}
        <div className="text-center mb-12">
          <h2
            className="text-white tracking-tight font-extrabold text-center mb-1 leading-tight text-h2"
            style={{ letterSpacing: "-1px" }}
          >
            Bracket 101
          </h2>
          <p className="text-center text-white/70 text-base sm:text-lgmax-w-2xl  mx-auto px40">
            Your <span className="hidden sm:inline" />
            guide to Bracky & the Bracket network
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-12">
          {visibleSections.map((section, sIdx) => (
            <FAQSection
              key={section.id}
              section={section}
              sectionIndex={sIdx}
              openId={openId}
              onToggleFAQ={toggleFAQ}
            />
          ))}
        </div>
      </div>
    </section>
  )
})
